import React, { useEffect, useMemo, useState } from "react";
import { authStore } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { X, Loader2, FileText, Image as ImageIcon, Save, Check, Settings2, Plus, Trash2, Wand2, MapPin, Mail, History } from "lucide-react";
import { fmtMD } from "../lib/date";
import { roiRange } from "../lib/roi";

const money = (n) => "$" + Math.round(Number(n || 0)).toLocaleString();
const num = (v) => Math.max(0, Number(v) || 0);
const parseLayout = (s) => { const m = String(s || "").match(/(\d+)\s*B\s*\/\s*(\d+)\s*BA/i); return m ? { bed: +m[1], bath: +m[2] } : { bed: 0, bath: 0 }; };

function computeEstimate(tpl, st) {
  const regions = tpl.regions || [];
  const region = regions.find((r) => r.key === st.regionKey) || regions[0] || { name: "", tiers: [] };
  const tiers = region.tiers || [];
  let perSf, tierName;
  if (num(st.customPerSf) > 0) { perSf = num(st.customPerSf); tierName = "特殊价 Custom"; }
  else { const t = tiers.find((x) => x.key === st.tierKey) || tiers[0] || { perSf: 0, name: "" }; perSf = t.perSf; tierName = t.name; }
  const sf = num(st.sf);
  const bed = num(st.bedrooms), bath = num(st.bathrooms);
  const doorCount = st.doorCount != null && st.doorCount !== "" ? num(st.doorCount) : (bed ? bed + 2 : 0);
  const base = Math.round(perSf * sf);
  const pkgName = `${region.name} · ${tierName}`;
  const lines = [{ key: "base", name: `${pkgName} · ${sf} sf × $${perSf}/sf`, amount: base }];
  let up = 0;
  (tpl.upgrades || []).forEach((u) => {
    if (!st.ups.has(u.key)) return;
    const ov = st.upPrices ? st.upPrices[u.key] : undefined;
    const price = (ov != null && ov !== "") ? num(ov) : u.price;
    let amt, suffix = "";
    if (u.unit === "per_sf") { amt = Math.round(price * sf); suffix = ` ($${price}/sf × ${sf})`; }
    else if (u.unit === "per_door") { amt = Math.round(price * doorCount); suffix = ` ($${price} × ${doorCount} 门)`; }
    else if (u.unit === "per_bath") { amt = Math.round(price * bath); suffix = ` ($${price} × ${bath} 卫)`; }
    else { amt = Math.round(price); }
    up += amt; lines.push({ key: u.key, name: u.name + suffix, amount: amt });
  });
  (st.customItems || []).forEach((ci, i) => {
    const nm = String(ci.name || "").trim(), pr = Math.round(num(ci.price));
    if (nm && pr) { up += pr; lines.push({ key: `custom_${i}`, name: nm, amount: pr }); }
  });
  const total = base + up;
  // ADU 投资口径：毛收益率区间（默认 12%–15%）→ 年租金 = 造价 × 收益率；回本 = 1 ÷ 收益率（毛）；市值 = 造价 × 2；净增值 = 市值 − 造价
  const r = { grossYieldLow: 0.12, grossYieldHigh: 0.15, valueAddMultiple: 2, expenseRatio: 0.25, ...(tpl.roi || {}) };
  let lo = Number(r.grossYieldLow) || 0.12, hi = Number(r.grossYieldHigh) || 0.15;
  if (hi < lo) [lo, hi] = [hi, lo];
  const mid = (lo + hi) / 2;
  const annual = Math.round(total * mid);
  const net = Math.round(annual * (1 - (Number(r.expenseRatio) || 0)));
  const valueAdd = Math.round(total * (Number(r.valueAddMultiple) || 2));
  const pb = (y) => (y > 0 && total > 0 ? Math.round((1 / y) * 10) / 10 : null);
  const roi = { monthlyRent: Math.round(annual / 12), monthlyRentLow: Math.round((total * lo) / 12), monthlyRentHigh: Math.round((total * hi) / 12),
    annualRent: annual, netAnnualIncome: net, valueAdd, valueAddMultiple: Number(r.valueAddMultiple) || 2, netEquityGain: valueAdd - total,
    paybackYears: pb(mid), paybackLow: pb(hi), paybackHigh: pb(lo),
    grossYield: Math.round(mid * 1000) / 10, grossYieldLow: Math.round(lo * 1000) / 10, grossYieldHigh: Math.round(hi * 1000) / 10 };
  return { base, upgradesTotal: up, lineItems: lines, total, roi, perSf, tierName, packageName: pkgName, doorCount };
}

const LOCK_LABEL_ZH = { sent: "发送给业主", viewed: "被业主查看", contract: "转为合同" };
const LOCK_LABEL_EN = { sent: "sent to the owner", viewed: "viewed by the owner", contract: "converted to a contract" };

export default function EstimateBuilder({ leadId, api, zh, onClose, onSaved, existing, leadRegion, intakePrefill }) {
  const { user } = useApp();
  const [tpl, setTpl] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [showTpl, setShowTpl] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const canViewLog = ["admin", "ceo", "sales_vp", "sales_manager"].includes(user?.role);
  const ex = existing?.estimate || {};
  // 分公司员工：按所属分公司定价，不可选分区；总部 hq / 未设区域 / admin / ceo：可选
  const userRegion = String(user?.region || "").toLowerCase();
  const isHq = !userRegion || userRegion === "hq" || ["admin", "ceo"].includes(user?.role);
  // 新报价默认按销售人员自己所在公司(区域)选单价; 已存报价沿用其保存值; 其次用客户所在区
  const [regionKey, setRegionKey] = useState(isHq ? (ex.regionKey || leadRegion || "la") : userRegion);
  const [tierKey, setTierKey] = useState(ex.tierKey && ex.tierKey !== "custom" ? ex.tierKey : "standard");
  const [customPerSf, setCustomPerSf] = useState(ex.tierKey === "custom" ? ex.perSf : "");
  const [sf, setSf] = useState(ex.sf || 950);
  const [layoutMode, setLayoutMode] = useState(ex.bedrooms ? "custom" : "standard");
  const [layout, setLayout] = useState(ex.layout || "");
  const [bedrooms, setBedrooms] = useState(ex.bedrooms || 2);
  const [bathrooms, setBathrooms] = useState(ex.bathrooms || 2);
  const [doorCount, setDoorCount] = useState(ex.doorCount != null ? ex.doorCount : "");
  const [ceiling, setCeiling] = useState(ex.ceiling || "9ft");
  const [ups, setUps] = useState(new Set((ex.lineItems || []).map((l) => l.key).filter((k) => k !== "base" && !k.startsWith("custom_"))));
  const [upPrices, setUpPrices] = useState(ex.upgradePrices ? { ...ex.upgradePrices } : {});
  const [customItems, setCustomItems] = useState((ex.lineItems || []).filter((l) => l.key?.startsWith("custom_")).map((l) => ({ name: l.name, price: l.amount })));
  const [saved, setSaved] = useState(existing || null);
  const [estStatus, setEstStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [aiDesc, setAiDesc] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [layoutPlans, setLayoutPlans] = useState([]);
  const [layoutPlanRef, setLayoutPlanRef] = useState(ex.layoutPlanRef || null);
  useEffect(() => { api.get(`/crm/leads/${leadId}/layout-plans`).then(({ data }) => setLayoutPlans(data || [])).catch(() => {}); }, [leadId]); // eslint-disable-line
  const loadStatus = async (id) => { try { const { data } = await api.get(`/crm/estimate/${id}/status`); setEstStatus(data); return data; } catch { return null; } };
  useEffect(() => { if (existing?.id) loadStatus(existing.id); }, []); // eslint-disable-line
  // 保存后后台生成 AI 分析 + PDF：轮询进度直至完成
  const genPending = estStatus?.gen && estStatus.gen.status === "pending";
  useEffect(() => {
    if (!saved?.id || !genPending) return undefined;
    const t = setInterval(() => loadStatus(saved.id), 2500);
    return () => clearInterval(t);
  }, [saved?.id, genPending]); // eslint-disable-line
  useEffect(() => { if (estStatus?.aiRoiText && saved && estStatus.aiRoiText !== saved.aiRoiText) setSaved((s) => ({ ...s, aiRoiText: estStatus.aiRoiText })); }, [estStatus?.aiRoiText]); // eslint-disable-line

  useEffect(() => {
    api.get("/crm/estimate-template").then((r) => {
      setTpl(r.data.template); setCanEdit(r.data.canEdit);
      if (!r.data.template.regions?.find((x) => x.key === regionKey)) setRegionKey(isHq ? (r.data.template.regions?.[0]?.key || "la") : (leadRegion || r.data.template.regions?.[0]?.key || "la"));
    }).catch(() => toast.error(zh ? "模板加载失败" : "Failed to load template"));
  }, []); // eslint-disable-line

  // 标准户型选中时解析卧室/卫生间
  const effBed = layoutMode === "standard" ? parseLayout(layout).bed || bedrooms : num(bedrooms);
  const effBath = layoutMode === "standard" ? parseLayout(layout).bath || bathrooms : num(bathrooms);
  const effLayout = layoutMode === "standard" ? layout : `${num(bedrooms)}B/${num(bathrooms)}BA`;

  const st = { regionKey, tierKey, customPerSf, sf, bedrooms: effBed, bathrooms: effBath, layout: effLayout, ceiling, ups, upPrices, customItems, doorCount };
  const calc = useMemo(() => (tpl ? computeEstimate(tpl, st) : null), [tpl, regionKey, tierKey, customPerSf, sf, layoutMode, layout, bedrooms, bathrooms, doorCount, ceiling, ups, upPrices, customItems]); // eslint-disable-line
  const region = tpl?.regions?.find((r) => r.key === regionKey) || tpl?.regions?.[0];

  const toggleUp = (k) => setUps((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const autoDoors = calc?.doorCount ?? 0;

  const aiDraft = async () => {
    if (!aiDesc.trim()) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/crm/ai-estimate-draft", { description: aiDesc });
      const p = data.params;
      if (p.regionKey && isHq) setRegionKey(p.regionKey);
      if (p.tierKey) { setTierKey(p.tierKey); setCustomPerSf(""); }
      setSf(p.sf);
      if (p.bedrooms || p.bathrooms) { setLayoutMode("custom"); setBedrooms(p.bedrooms || 2); setBathrooms(p.bathrooms || 2); }
      else if (p.layout) { setLayoutMode("standard"); setLayout(p.layout); }
      if (p.ceiling) setCeiling(p.ceiling);
      setUps(new Set(p.upgrades || []));
      toast.success(zh ? "AI 已生成报价初稿，请确认" : "AI draft ready — please review");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "AI 暂不可用" : "AI unavailable")); }
    setAiBusy(false);
  };

  const save = async () => {
    setBusy("save");
    try {
      const body = { regionKey, tierKey, customPerSf: num(customPerSf), sf: num(sf), layout: effLayout,
        bedrooms: effBed, bathrooms: effBath, doorCount: doorCount === "" ? autoDoors : num(doorCount),
        ceiling, upgrades: [...ups], upgradePrices: upPrices, customItems: customItems.filter((c) => c.name && num(c.price)), layoutPlanRef,
        lang: zh ? "zh" : "en", baseUrl: window.location.origin, id: saved?.id || undefined };
      const { data } = await api.post(`/crm/leads/${leadId}/estimate/save`, body);
      setSaved(data); onSaved?.(); setEstStatus({ gen: data.gen || { status: "pending" } }); loadStatus(data.id);
      if (data.savedAsNew) toast.info(zh ? `原报价已${LOCK_LABEL_ZH[data.savedAsNewReason] || "对外生效"}，已另存为新版本 v${data.version}（业主看到的原版本保持不变）` : `Original was ${data.savedAsNewReason}; saved as new version v${data.version}`);
      else if (data.updatedInPlace) toast.success(zh ? "报价已原地更新，正在后台重新生成 AI 投资分析与报价 PDF" : "Estimate updated — regenerating AI analysis & PDF");
      else toast.success(zh ? "报价已保存，正在后台生成 AI 投资分析与报价 PDF" : "Saved — generating AI analysis & PDF in background");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed")); }
    setBusy("");
  };
  const locked = !!estStatus?.locked;
  const fileUrl = (fid) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;
  const previewPdf = async () => {
    if (!saved) { toast.error(zh ? "请先保存报价" : "Save first"); return; }
    let fid = estStatus?.pdfFileId || saved.pdfFileId;
    if (!fid) {
      setBusy("pdf");
      try { const { data } = await api.post(`/crm/estimate/${saved.id}/pdf`, { lang: zh ? "zh" : "en", baseUrl: window.location.origin }); fid = data.fileId; loadStatus(saved.id); onSaved?.(); }
      catch (e) { toast.error(e?.detailText || (zh ? "生成失败" : "Failed")); setBusy(""); return; }
      setBusy("");
    }
    window.open(fileUrl(fid), "_blank", "noopener");
  };
  const toContractDraft = async () => {
    if (!saved) { toast.error(zh ? "请先保存报价" : "Save first"); return; }
    setBusy("contract");
    try {
      const { data } = await api.post(`/crm/leads/${leadId}/contract/create-draft`, { estimateId: saved.id });
      toast.success(zh ? `已带入合同草稿（总价 $${Number(data.total || 0).toLocaleString()}，${data.items || 0} 项明细），请在 ④ 合同 中查看/编辑` : "Contract draft created — see the Contract section");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "生成失败" : "Failed")); }
    setBusy("");
  };
  const runAction = async (kind) => {
    if (!saved) { toast.error(zh ? "请先保存报价" : "Save first"); return; }
    setBusy(kind);
    try {
      if (kind === "img") { await api.post(`/crm/estimate/${saved.id}/ai-rendering`, {}); toast.success(zh ? "AI 效果图已生成并附加" : "Rendering generated"); }
      if (kind === "floor") { await api.post(`/crm/estimate/${saved.id}/ai-floorplan`, {}); toast.success(zh ? "AI 户型草图已生成并附加" : "Floor plan generated"); }
      if (kind === "send") {
        let email = "";
        try {
          const { data } = await api.post(`/crm/estimate/${saved.id}/send-email`, { baseUrl: window.location.origin });
          if (data.ok) toast.success(zh ? `报价单已发送至业主 ${data.to}` : `Sent to ${data.to}`);
          else toast.warning(zh ? `邮箱服务未配置，请复制链接发给业主：${data.link}` : `Email not configured: ${data.link}`);
          loadStatus(saved.id);
        } catch (err) {
          if (err?.response?.status === 400) {
            email = window.prompt(zh ? "请输入业主邮箱以发送报价单" : "Enter owner's email to send");
            if (email && email.trim()) {
              const { data } = await api.post(`/crm/estimate/${saved.id}/send-email`, { email: email.trim(), baseUrl: window.location.origin });
              if (data.ok) toast.success(zh ? `报价单已发送至 ${data.to}` : `Sent to ${data.to}`);
              else toast.warning(zh ? `邮箱服务未配置，请复制链接发给业主：${data.link}` : `Email not configured: ${data.link}`);
              loadStatus(saved.id);
            }
          } else { throw err; }
        }
      }
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "生成失败" : "Failed")); }
    setBusy("");
  };
  const saveTpl = async () => {
    setBusy("tpl");
    try { const { data } = await api.put("/crm/estimate-template", { template: tpl }); if (data.template) setTpl(data.template); toast.success(zh ? `模板已保存 · 记录 ${data.changes?.length || 0} 项改动` : `Template saved · ${data.changes?.length || 0} changes logged`); setShowTpl(false); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed")); }
    setBusy("");
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-3" data-testid="estimate-builder" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-900 text-white">
          <div className="flex items-center gap-2"><span className="font-bold text-red-500">ALIGCUS</span><span className="text-sm">{zh ? "在线报价 Estimate" : "Online Estimate"}</span></div>
          <div className="flex items-center gap-2">
            {canEdit && <button data-testid="estimate-edit-template" onClick={() => { setShowTpl((v) => !v); setShowLog(false); }} className="text-slate-300 hover:text-white flex items-center gap-1 text-xs"><Settings2 className="h-4 w-4" />{zh ? "编辑模板" : "Template"}</button>}
            {canViewLog && <button data-testid="estimate-template-log" onClick={() => { setShowLog((v) => !v); setShowTpl(false); }} className={`flex items-center gap-1 text-xs ${showLog ? "text-white" : "text-slate-300 hover:text-white"}`}><History className="h-4 w-4" />{zh ? "改动记录" : "Change log"}</button>}
            <button data-testid="estimate-close" onClick={onClose} className="text-slate-300 hover:text-white"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {!tpl ? (
          <div className="flex-1 flex items-center justify-center p-16"><Loader2 className="h-6 w-6 animate-spin text-brand-500" /></div>
        ) : showTpl ? (
          <TemplateEditor tpl={tpl} setTpl={setTpl} zh={zh} onSave={saveTpl} busy={busy === "tpl"} onCancel={() => setShowTpl(false)} />
        ) : showLog ? (
          <TemplateChangeLog api={api} zh={zh} onClose={() => setShowLog(false)} />
        ) : (
          <div className="flex-1 overflow-auto p-5 space-y-5">
            {saved?.id && estStatus && (locked || estStatus.revisionCount > 0 || estStatus.version > 1) && (
              <div className={`rounded-xl border px-3 py-2 text-[12px] flex items-start gap-2 ${locked ? "border-amber-200 bg-amber-50 text-amber-800" : "border-slate-200 bg-slate-50 text-slate-600"}`} data-testid="estimate-version-notice" data-locked={locked ? "1" : "0"} data-version={estStatus.version || 1}>
                <span className="font-semibold shrink-0">v{estStatus.version || 1}</span>
                <div className="space-y-0.5">
                  {locked && <p data-testid="estimate-locked-notice">{zh ? `该报价已${LOCK_LABEL_ZH[estStatus.lockReason] || "对外生效"}，为保护业主已看到的版本，修改后保存将自动另存为新版本 v${(estStatus.version || 1) + 1}（原版本与业主链接保持不变）` : `This estimate was ${LOCK_LABEL_EN[estStatus.lockReason] || "published"}; saving will create version v${(estStatus.version || 1) + 1} and keep the owner's copy unchanged.`}</p>}
                  {!locked && estStatus.revisionCount > 0 && <p data-testid="estimate-revision-info">{zh ? `已原地更新 ${estStatus.revisionCount} 次 · 最近 ${estStatus.updatedBy || ""} ${fmtMD(estStatus.updatedAt)}` : `Updated in place ${estStatus.revisionCount}× · last by ${estStatus.updatedBy || ""} ${fmtMD(estStatus.updatedAt)}`}{estStatus.revisions?.length ? ` · ${zh ? "上一版" : "prev"} ${money(estStatus.revisions[estStatus.revisions.length - 1].prevTotal)} → ${money(estStatus.revisions[estStatus.revisions.length - 1].total)}` : ""}</p>}
                  {!locked && !estStatus.revisionCount && estStatus.version > 1 && <p>{zh ? "本条为另存的新版本；保存将原地更新本条" : "New version; saving updates this record in place"}</p>}
                </div>
              </div>
            )}
            {/* AI draft */}
            <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3" data-testid="estimate-ai-draft">
              <div className="flex items-center gap-1.5 mb-2"><Wand2 className="h-4 w-4 text-indigo-500" /><span className="text-xs font-semibold text-indigo-700">{zh ? "AI 起草报价" : "AI Draft"}</span></div>
              <div className="flex gap-2">
                <input data-testid="estimate-ai-desc" value={aiDesc} onChange={(e) => setAiDesc(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && aiDraft()}
                  placeholder={zh ? "用一句话描述业主需求，如：洛杉矶后院四室三卫约1150尺标准版，要智能马桶" : "Describe the owner's needs in one sentence"}
                  className="flex-1 text-sm rounded-lg border border-indigo-200 px-3 py-2 focus:border-indigo-400 focus:outline-none" />
                <Button data-testid="estimate-ai-draft-btn" onClick={aiDraft} disabled={aiBusy || !aiDesc.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="h-4 w-4 mr-1" />{zh ? "生成" : "Draft"}</>}
                </Button>
              </div>
            </div>

            {layoutPlans.length > 0 && (
              <div data-testid="estimate-layout-ref">
                <p className="text-xs font-semibold text-slate-500 mb-2">{zh ? "引用布局方案（卫星图）" : "Reference layout plan"}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <select data-testid="estimate-layout-select" value={layoutPlanRef?.id || ""}
                    onChange={(e) => { const p = layoutPlans.find((x) => x.id === e.target.value); setLayoutPlanRef(p ? { id: p.id, name: p.name, fileId: p.fileId } : null); }}
                    className="text-sm rounded-lg border border-slate-200 px-2.5 py-1.5">
                    <option value="">{zh ? "不引用" : "None"}</option>
                    {layoutPlans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  {layoutPlanRef?.fileId && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${layoutPlanRef.fileId}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`} target="_blank" rel="noreferrer" data-testid="estimate-layout-thumb">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${layoutPlanRef.fileId}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`} alt={layoutPlanRef.name} className="h-14 w-14 object-cover rounded-md border border-slate-200" />
                    </a>
                  )}
                  {(() => { const p = layoutPlans.find((x) => x.id === layoutPlanRef?.id); if (!p) return null; return (
                    <>
                      {p.aduSf ? (
                        <Button size="sm" type="button" data-testid="estimate-apply-plan-area" onClick={() => { setSf(Math.round(p.aduSf)); toast.success(zh ? `已将方案面积 ${Math.round(p.aduSf).toLocaleString()} sf 填入报价` : `Applied ${Math.round(p.aduSf)} sf`); }} className="h-8 bg-slate-800 hover:bg-slate-900 text-white text-xs">
                          {zh ? `填入面积 ${Math.round(p.aduSf).toLocaleString()} sf` : `Use ${Math.round(p.aduSf)} sf`}
                        </Button>
                      ) : null}
                      {p.aduSf && p.perSf ? (
                        <Button size="sm" type="button" data-testid="estimate-apply-plan-quote" onClick={() => { setSf(Math.round(p.aduSf)); setCustomPerSf(String(Math.round(p.perSf))); toast.success(zh ? `已套用方案：${Math.round(p.aduSf).toLocaleString()} sf × $${Math.round(p.perSf)}/sf ≈ $${Math.round(p.aduSf * p.perSf).toLocaleString()}` : `Applied plan quote ≈ $${Math.round(p.aduSf * p.perSf)}`); }} className="h-8 bg-brand-500 hover:bg-brand-600 text-white text-xs">
                          {zh ? `套用方案报价 ≈ $${Math.round(p.aduSf * p.perSf).toLocaleString()}` : `Draft quote ≈ $${Math.round(p.aduSf * p.perSf)}`}
                        </Button>
                      ) : null}
                    </>
                  ); })()}
                  <span className="text-[11px] text-slate-400">{zh ? "选中后将随报价展示给业主" : "Shown with the quote"}</span>
                </div>
              </div>
            )}


            {/* Region：分公司员工锁定所属分公司；总部可选 */}
            {isHq ? (
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">{zh ? "选择分区 Region" : "Region"}</p>
              <div className="grid grid-cols-3 gap-2">
                {tpl.regions.map((r) => (
                  <button key={r.key} data-testid={`estimate-region-${r.key}`} onClick={() => setRegionKey(r.key)}
                    className={`rounded-xl border p-2.5 text-sm font-semibold transition ${regionKey === r.key ? "border-red-500 bg-red-50 text-red-700 ring-1 ring-red-200" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
                    <MapPin className="h-3.5 w-3.5 inline mr-1 opacity-60" />{r.name}
                  </button>
                ))}
              </div>
            </div>
            ) : (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 flex items-center gap-2" data-testid="estimate-region-locked" data-region={regionKey}>
              <MapPin className="h-4 w-4 opacity-70" />{zh ? "分公司报价：" : "Branch pricing: "}{region?.name || regionKey}
              <span className="text-[11px] font-normal text-red-500">{zh ? "按您所属分公司自动套用单价" : "priced by your branch"}</span>
            </div>
            )}

            {/* Tier */}
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">{zh ? "选择档位 Package" : "Package"}</p>
              <div className="grid grid-cols-3 gap-2">
                {(region?.tiers || []).map((t) => (
                  <button key={t.key} data-testid={`estimate-tier-${t.key}`} onClick={() => { setTierKey(t.key); setCustomPerSf(""); }}
                    className={`text-left rounded-xl border p-3 transition ${num(customPerSf) <= 0 && tierKey === t.key ? "border-red-500 bg-red-50 ring-1 ring-red-200" : "border-slate-200 hover:border-slate-300"}`}>
                    <p className="text-sm font-semibold text-slate-800">{t.name}</p>
                    <p className="text-[12px] text-red-600 font-bold mt-0.5">${t.perSf}/sf</p>
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className={`text-[11px] ${num(customPerSf) > 0 ? "text-red-600 font-semibold" : "text-slate-400"}`}>{zh ? "或手动输入特殊单价 Custom $/sf" : "Custom $/sf"}</span>
                <Input data-testid="estimate-custom-persf" type="number" value={customPerSf} onChange={(e) => setCustomPerSf(e.target.value)} placeholder="—" className="h-8 w-28" />
                {num(customPerSf) > 0 && <span className="text-[11px] text-red-500">{zh ? "已启用特殊价" : "custom active"}</span>}
              </div>
            </div>

            {/* Params */}
            {intakePrefill && (intakePrefill.sf || intakePrefill.roomType || intakePrefill.zoning || intakePrefill.aduType) && (
              <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-2.5 flex items-center justify-between gap-2" data-testid="estimate-intake-banner">
                <p className="text-[11px] text-indigo-700 truncate">
                  {zh ? "现场调研：" : "Intake: "}
                  {intakePrefill.aduType ? `${intakePrefill.aduType} · ` : ""}
                  {intakePrefill.zoning ? `Zoning ${intakePrefill.zoning} · ` : ""}
                  {intakePrefill.roomType ? `${intakePrefill.roomType} · ` : ""}
                  {intakePrefill.sf ? `${intakePrefill.sf} sf` : ""}
                </p>
                <button data-testid="estimate-intake-apply" onClick={() => {
                  if (intakePrefill.sf) setSf(intakePrefill.sf);
                  const m = /(\d+)\s*B\s*(\d+)\s*B/i.exec(intakePrefill.roomType || "");
                  if (m) { setLayoutMode("custom"); setBedrooms(Number(m[1])); setBathrooms(Number(m[2])); }
                  toast.success(zh ? "已带入调研数据" : "Applied intake");
                }} className="shrink-0 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 border border-indigo-200 rounded-full px-2.5 py-1 bg-white">{zh ? "带入面积/房型" : "Apply"}</button>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><label className="text-[11px] text-slate-500">{zh ? "面积 (sf)" : "Size (sf)"}</label>
                <Input data-testid="estimate-sf" type="number" value={sf} onChange={(e) => setSf(e.target.value)} className="h-9" /></div>
              <div><label className="text-[11px] text-slate-500">{zh ? "层高 Ceiling" : "Ceiling"}</label>
                <select data-testid="estimate-ceiling" value={ceiling} onChange={(e) => setCeiling(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
                  {tpl.ceilings.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="text-[11px] text-slate-500">{zh ? "室内门数量 Doors" : "Doors"}</label>
                <Input data-testid="estimate-doors" type="number" value={doorCount} onChange={(e) => setDoorCount(e.target.value)} placeholder={`${autoDoors} (${zh ? "自动" : "auto"})`} className="h-9" /></div>
            </div>

            {/* Layout */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-500">{zh ? "户型 Layout" : "Layout"}</p>
                <div className="flex rounded-lg border border-slate-200 overflow-hidden text-[11px]">
                  <button data-testid="estimate-layout-standard" onClick={() => setLayoutMode("standard")} className={`px-2.5 py-1 ${layoutMode === "standard" ? "bg-slate-800 text-white" : "text-slate-500"}`}>{zh ? "标准户型" : "Standard"}</button>
                  <button data-testid="estimate-layout-custom" onClick={() => setLayoutMode("custom")} className={`px-2.5 py-1 ${layoutMode === "custom" ? "bg-slate-800 text-white" : "text-slate-500"}`}>{zh ? "自定义" : "Custom"}</button>
                </div>
              </div>
              {layoutMode === "standard" ? (
                <select data-testid="estimate-layout" value={layout} onChange={(e) => setLayout(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
                  <option value="">{zh ? "请选择" : "Select"}</option>
                  {tpl.layouts.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[11px] text-slate-500">{zh ? "卧室数 Bedrooms" : "Bedrooms"}</label>
                    <Input data-testid="estimate-bedrooms" type="number" value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} className="h-9" /></div>
                  <div><label className="text-[11px] text-slate-500">{zh ? "卫生间数 Bathrooms" : "Bathrooms"}</label>
                    <Input data-testid="estimate-bathrooms" type="number" value={bathrooms} onChange={(e) => setBathrooms(e.target.value)} className="h-9" /></div>
                </div>
              )}
            </div>

            {/* Upgrades — grouped by category, prices editable (recommended defaults) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-500">{zh ? "升级项 Upgrades" : "Upgrades"}</p>
                <span className="text-[10px] text-slate-400">{zh ? "价格为推荐价，可直接修改" : "Prices are recommended · editable"}</span>
              </div>
              {(() => {
                const CAT_LABEL = zh
                  ? { kitchen_bath: "厨卫", structure: "结构", electrical: "电气", exterior: "外观", other: "其他" }
                  : { kitchen_bath: "Kitchen & Bath", structure: "Structure", electrical: "Electrical", exterior: "Exterior", other: "Other" };
                const CAT_ORDER = ["kitchen_bath", "structure", "electrical", "exterior", "other"];
                const groups = {};
                (tpl.upgrades || []).forEach((u) => { const c = u.category || "other"; (groups[c] = groups[c] || []).push(u); });
                return CAT_ORDER.filter((c) => groups[c]?.length).map((c) => (
                  <div key={c} className="mb-3" data-testid={`upgrade-group-${c}`}>
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">{CAT_LABEL[c]}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {groups[c].map((u) => (
                        <div key={u.key} data-testid={`estimate-upgrade-${u.key}`} className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${ups.has(u.key) ? "border-indigo-400 bg-indigo-50" : "border-slate-200"}`}>
                          <span className="flex items-center gap-2 flex-1 cursor-pointer min-w-0" onClick={() => toggleUp(u.key)}>
                            <input type="checkbox" data-testid={`estimate-upgrade-check-${u.key}`} checked={ups.has(u.key)} readOnly />
                            {/* 需求 FB54f452b7e3：新增的「分电表」没有历史价，price 先为 0，
                                需要销售自己填。名称在窄屏会被 truncate 截断，补 title 让鼠标悬停能看到全称，
                                避免「看到 0 却不知道这一项到底是什么」。 */}
                            <span className="truncate" title={u.name}>{u.name}</span>
                          </span>
                          <span className="flex items-center gap-0.5 text-slate-500 text-[11px] shrink-0">
                            +$<Input type="number" data-testid={`estimate-upgrade-price-${u.key}`}
                              value={upPrices[u.key] != null ? upPrices[u.key] : u.price}
                              onChange={(e) => setUpPrices((p) => ({ ...p, [u.key]: e.target.value }))}
                              className="w-16 h-6 px-1 text-right text-[11px]" />
                            {u.unit === "per_sf" ? "/sf" : u.unit === "per_door" ? "/门" : u.unit === "per_bath" ? "/卫" : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
              {/* Custom items */}
              <div className="mt-2 space-y-2" data-testid="estimate-custom-items">
                {customItems.map((ci, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input data-testid={`estimate-custom-name-${i}`} value={ci.name} onChange={(e) => setCustomItems((a) => a.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder={zh ? "自定义需求名称" : "Custom item"} className="h-8 flex-1" />
                    <Input data-testid={`estimate-custom-price-${i}`} type="number" value={ci.price} onChange={(e) => setCustomItems((a) => a.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} placeholder="$" className="h-8 w-28" />
                    <button data-testid={`estimate-custom-del-${i}`} onClick={() => setCustomItems((a) => a.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <button data-testid="estimate-add-custom" onClick={() => setCustomItems((a) => [...a, { name: "", price: "" }])} className="text-xs text-indigo-600 flex items-center gap-1"><Plus className="h-3.5 w-3.5" />{zh ? "自定义添加需求" : "Add custom item"}</button>
              </div>
            </div>

            {/* Live total + ROI */}
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-slate-600">{zh ? "预估总价 Total" : "Total"} <span className="text-[11px] text-slate-400">({calc.tierName} · ${calc.perSf}/sf)</span></span>
                <span className="text-2xl font-bold text-red-600" data-testid="estimate-total">{money(calc.total)}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-center" data-testid="estimate-roi">
                {[["月租金", "Rent/mo", roiRange(calc.roi, "monthlyRentLow", "monthlyRentHigh", "monthlyRent", money), zh ? `年租金 = 造价 × ${calc.roi.grossYieldLow}%–${calc.roi.grossYieldHigh}%` : "annual rent = cost × yield"],
                  ["回本", "Payback", roiRange(calc.roi, "paybackLow", "paybackHigh", "paybackYears", (v) => v, zh ? "年" : "y"), zh ? "造价 ÷ 年租金" : "cost ÷ annual rent"],
                  ["毛收益率", "Yield", roiRange(calc.roi, "grossYieldLow", "grossYieldHigh", "grossYield", (v) => `${v}%`), zh ? "年租金 ÷ 造价" : "annual rent ÷ cost"],
                  ["净增值", "Equity+", money(calc.roi.netEquityGain), zh ? `建成市值 ${money(calc.roi.valueAdd)}（造价 × ${calc.roi.valueAddMultiple}）` : `ADU value ${money(calc.roi.valueAdd)} (${calc.roi.valueAddMultiple}× cost)`]].map((m, i) => (
                  <div key={i} className="rounded-lg bg-white border border-slate-100 py-2 px-1" data-testid={`estimate-roi-${["rent", "payback", "yield", "equity"][i]}`}>
                    <p className="text-[10px] text-slate-400">{zh ? m[0] : m[1]}</p>
                    <p className="text-sm font-semibold text-slate-800">{m[2]}</p>
                    <p className="text-[9px] text-slate-400 truncate" title={m[3]}>{m[3]}</p>
                  </div>
                ))}
              </div>
            </div>
            {saved?.id && (estStatus?.gen || saved.gen) && (() => { const g = estStatus?.gen || saved.gen || {}; const st = (k) => g[k] === "done" ? (zh ? "完成" : "done") : g[k] === "failed" ? (zh ? "失败" : "failed") : (zh ? "生成中…" : "running…"); return (
              <div className={`rounded-lg border px-3 py-2 text-[11px] flex items-center gap-3 flex-wrap ${g.status === "pending" ? "border-indigo-200 bg-indigo-50 text-indigo-700" : g.status === "failed" ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`} data-testid="estimate-gen-progress" data-status={g.status || "pending"}>
                {g.status === "pending" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                <span>{zh ? "保存后自动生成" : "Auto-generated on save"}:</span>
                <span data-testid="estimate-gen-roi-state">{zh ? "AI 投资分析" : "AI ROI"} · {st("roi")}</span>
                <span data-testid="estimate-gen-pdf-state">{zh ? "报价 PDF" : "Quote PDF"} · {st("pdf")}</span>
                {g.pdfError && <span className="text-red-600 truncate max-w-[260px]" title={g.pdfError}>{g.pdfError}</span>}
              </div>
            ); })()}
            {saved?.aiRoiText && (
              <div className="rounded-xl border border-violet-200 bg-violet-50 p-3 text-[12px] text-slate-700 whitespace-pre-line" data-testid="estimate-ai-roi-text">{saved.aiRoiText}</div>
            )}
          </div>
        )}

        {!showTpl && tpl && (
          <div className="border-t border-slate-100 p-3 flex flex-wrap items-center gap-2 bg-white">
            <Button data-testid="estimate-save" onClick={save} disabled={busy === "save"} className="bg-brand-500 hover:bg-brand-600 text-white">
              {busy === "save" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : saved ? <Check className="h-4 w-4 mr-1" /> : <Save className="h-4 w-4 mr-1" />}{saved ? (locked ? (zh ? "另存新版本" : "Save as new version") : (zh ? "更新报价" : "Update")) : (zh ? "保存报价" : "Save")}
            </Button>
            <div className="h-6 w-px bg-slate-200 mx-1" />
            <Button data-testid="estimate-preview-pdf" variant="outline" disabled={!saved || busy === "pdf" || genPending} onClick={previewPdf} title={genPending ? (zh ? "PDF 正在后台生成，完成后可预览" : "PDF is being generated") : ""}>
              {busy === "pdf" || genPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}{genPending ? (zh ? "PDF 生成中…" : "Generating…") : (zh ? "预览报价PDF" : "Preview PDF")}
            </Button>
            <Button data-testid="estimate-to-contract" variant="outline" disabled={!saved || busy === "contract"} onClick={toContractDraft}>
              {busy === "contract" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1 text-red-500" />}{zh ? "转合同草稿" : "To Contract"}
            </Button>
            <div className="h-6 w-px bg-slate-200 mx-1" />
            <Button data-testid="estimate-send-owner" disabled={!saved || busy === "send"} onClick={() => runAction("send")} className="bg-slate-800 hover:bg-slate-900 text-white">
              {busy === "send" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}{zh ? "发业主" : "Send"}
            </Button>
            {estStatus?.lastSentAt && (
              <span className="text-[11px] text-slate-500" data-testid="estimate-sent-status">
                {estStatus.ownerViewCount > 0
                  ? <span className="text-blue-600" data-testid="estimate-owner-viewed">👁 {zh ? "业主已查看" : "Viewed"} {fmtMD(estStatus.ownerLastViewedAt)}（{estStatus.ownerViewCount}{zh ? "次" : "x"}）</span>
                  : <span>✉ {zh ? "已发送，业主未查看" : "Sent, not viewed"}</span>}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const DEFAULT_SPEC_GROUPS = [
  { name: "Structure", nameZh: "结构", items: [
    { label: "Foundation", value: "Concrete slab foundation with rebar reinforcement" },
    { label: "Framing", value: "2x4/6 wood framing, 16\" on center" },
    { label: "Ceiling Height", value: "9ft ceiling throughout" },
    { label: "Roof Structure", value: "Engineered trusses with plywood sheathing" },
    { label: "Exterior Walls", value: "Stucco finish over moisture barrier" }] },
  { name: "Electrical", nameZh: "电气", items: [
    { label: "Electrical Panel", value: "100 Amp main panel" },
    { label: "Outlets", value: "Standard 15A/20A outlets per code" },
    { label: "Lighting", value: "LED recessed lights throughout" },
    { label: "Smoke/CO Detectors", value: "Hardwired with battery backup" }] },
  { name: "Interior", nameZh: "室内", items: [
    { label: "Drywall", value: "5/8\" drywall, smooth finish" },
    { label: "Paint", value: "Dunn Edwards premium paint, 2 coats" },
    { label: "Flooring", value: "6.5mm vinyl plank flooring" },
    { label: "Interior Doors", value: "Solid-core interior doors" }] },
  { name: "Bathroom", nameZh: "卫浴", items: [
    { label: "Vanity", value: "36\" vanity with quartz top" },
    { label: "Toilets", value: "One-piece elongated toilets x2" },
    { label: "Shower", value: "Tile shower with glass door" }] },
  { name: "Insulation", nameZh: "保温隔热", items: [
    { label: "Wall Insulation", value: "JM (White) Fiberglass R-19" },
    { label: "Ceiling Insulation", value: "JM (White) Fiberglass R-38" }] },
  { name: "Plumbing", nameZh: "给排水", items: [
    { label: "Water Supply", value: "Copper piping throughout" },
    { label: "Drain Lines", value: "ABS/PVC drainage system" },
    { label: "Water Heater", value: "50-gallon tank water heater" }] },
  { name: "Kitchen", nameZh: "厨房", items: [
    { label: "Cabinets", value: "American style soft-close cabinets" },
    { label: "Countertops", value: "Quartz countertops" },
    { label: "Sink", value: "Stainless steel undermount sink" },
    { label: "Range", value: "Gas range with oven" }] },
  { name: "HVAC", nameZh: "空调通风", items: [
    { label: "Heating/Cooling", value: "Mini split system (2 zones)" },
    { label: "Ventilation", value: "Quiet bathroom exhaust fans" }] },
  { name: "Exterior", nameZh: "外部", items: [
    { label: "Windows", value: "Double-pane vinyl windows, Low-E" },
    { label: "Entry Door", value: "Upgraded insulated entry door" },
    { label: "Roofing", value: "Architectural shingle roofing" },
    { label: "Gutters", value: "Aluminum gutters and downspouts" }] },
];

function TierSpecEditor({ tpl, setTpl, zh }) {
  const tierKeys = (tpl.regions?.[0]?.tiers || []).map((t) => t.key);
  const keys = tierKeys.length ? tierKeys : ["basic", "standard", "premium"];
  const tierLabel = { basic: zh ? "基础版 Basic" : "Basic", standard: zh ? "标准版 Standard" : "Standard", premium: zh ? "高级版 Premium" : "Premium" };
  const [tier, setTier] = useState(keys.includes("premium") ? "premium" : keys[0]);
  const groups = (tpl.tierSpecGroups || {})[tier] || [];
  const commit = (g) => setTpl({ ...tpl, tierSpecGroups: { ...(tpl.tierSpecGroups || {}), [tier]: g } });
  const setG = (gi, k, v) => commit(groups.map((g, j) => j === gi ? { ...g, [k]: v } : g));
  const setItem = (gi, ii, k, v) => commit(groups.map((g, j) => j === gi ? { ...g, items: g.items.map((it, m) => m === ii ? { ...it, [k]: v } : it) } : g));
  const addItem = (gi) => commit(groups.map((g, j) => j === gi ? { ...g, items: [...(g.items || []), { label: "", value: "" }] } : g));
  const delItem = (gi, ii) => commit(groups.map((g, j) => j === gi ? { ...g, items: g.items.filter((_, m) => m !== ii) } : g));
  const addGroup = () => commit([...groups, { name: "New Group", nameZh: "新分类", items: [] }]);
  const delGroup = (gi) => commit(groups.filter((_, j) => j !== gi));
  const prefill = () => commit(JSON.parse(JSON.stringify(DEFAULT_SPEC_GROUPS)));
  return (
    <div className="pt-3 border-t border-slate-100" data-testid="tpl-spec-editor">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-slate-500">{zh ? "套餐规格清单（PDF 第 4-5 页）" : "Package Specifications (PDF p.4-5)"}</p>
        <div className="flex items-center gap-2">
          <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="tpl-spec-tier" className="h-8 rounded-md border border-slate-200 text-xs px-1">
            {keys.map((k) => <option key={k} value={k}>{tierLabel[k] || k}</option>)}
          </select>
          <button onClick={prefill} data-testid="tpl-spec-prefill" className="text-[11px] text-indigo-600 hover:underline">{zh ? "填充默认规格" : "Load defaults"}</button>
          <button onClick={addGroup} data-testid="tpl-spec-add-group" className="text-xs text-indigo-600 flex items-center gap-1"><Plus className="h-3.5 w-3.5" />{zh ? "分类" : "Group"}</button>
        </div>
      </div>
      {groups.length === 0 && <p className="text-[11px] text-slate-400 mb-2">{zh ? "该套餐暂无自定义规格（PDF 将使用系统默认清单）。点「填充默认规格」开始编辑。" : "No custom specs (PDF uses system defaults). Click Load defaults to start."}</p>}
      <div className="space-y-3">
        {groups.map((g, gi) => (
          <div key={gi} className="rounded-lg border border-slate-100 p-2" data-testid={`tpl-spec-group-${gi}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <Input value={g.name || ""} onChange={(e) => setG(gi, "name", e.target.value)} placeholder="EN name" className="h-7 flex-1 text-xs" data-testid={`tpl-spec-group-en-${gi}`} />
              <Input value={g.nameZh || ""} onChange={(e) => setG(gi, "nameZh", e.target.value)} placeholder="中文名称" className="h-7 flex-1 text-xs" data-testid={`tpl-spec-group-zh-${gi}`} />
              <button onClick={() => addItem(gi)} className="text-indigo-600 shrink-0" data-testid={`tpl-spec-add-item-${gi}`}><Plus className="h-4 w-4" /></button>
              <button onClick={() => delGroup(gi)} className="text-slate-300 hover:text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
            </div>
            {(g.items || []).map((it, ii) => (
              <div key={ii} className="flex items-center gap-2 mb-1 pl-2">
                <Input value={it.label || ""} onChange={(e) => setItem(gi, ii, "label", e.target.value)} placeholder={zh ? "项目" : "Label"} className="h-7 w-32 text-xs" />
                <Input value={it.value || ""} onChange={(e) => setItem(gi, ii, "value", e.target.value)} placeholder={zh ? "规格说明" : "Spec value"} className="h-7 flex-1 text-xs" />
                <button onClick={() => delItem(gi, ii)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplateChangeLog({ api, zh, onClose }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState({});
  useEffect(() => { api.get("/crm/estimate-template/changes?limit=100").then(({ data }) => setItems(data.items || [])).catch((e) => { toast.error(e?.detailText || (zh ? "加载失败" : "Failed")); setItems([]); }); }, [api]); // eslint-disable-line
  return (
    <div className="flex-1 overflow-auto p-5 space-y-3" data-testid="estimate-template-changelog">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-800">{zh ? "报价模板改动记录" : "Estimate template change log"}</p>
          <p className="text-[11px] text-slate-500">{zh ? "每次保存模板自动记录：ROI 参数 · 各分区档位 $/sf · 升级项新增 / 删除 / 改价 / 改名 · 户型 / 层高列表" : "Recorded on every template save: ROI params · tier $/sf · upgrade add/remove/price/name · layouts/ceilings"}</p>
        </div>
        <Button variant="outline" size="sm" data-testid="estimate-template-log-close" onClick={onClose}>{zh ? "返回报价" : "Back"}</Button>
      </div>
      {items === null && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
      {items && items.length === 0 && <p className="text-xs text-slate-400 py-8 text-center" data-testid="estimate-template-log-empty">{zh ? "暂无改动记录（自本功能上线后的模板保存才会记录）" : "No changes recorded yet"}</p>}
      {items && items.map((it) => {
        const isOpen = open[it.id] ?? true;
        return (
          <div key={it.id} className="rounded-lg border border-slate-200 bg-white" data-testid={`tpl-log-${it.id}`}>
            <button type="button" onClick={() => setOpen((o) => ({ ...o, [it.id]: !isOpen }))} className="w-full flex items-center gap-2 px-3 py-2 text-left">
              <span className="text-xs font-semibold text-slate-800">{fmtMD(it.at)} {String(it.at).slice(11, 16)}</span>
              <span className="text-xs text-slate-600">{it.by || it.byUser}</span>
              <span className="ml-auto text-[11px] px-1.5 rounded-full bg-slate-100 text-slate-600" data-testid={`tpl-log-count-${it.id}`}>{it.count} {zh ? "项" : "changes"}</span>
            </button>
            {isOpen && (
              <ul className="border-t border-slate-100 divide-y divide-slate-50 text-[12px]">
                {(it.changes || []).map((c, i) => (
                  <li key={i} className="px-3 py-1.5 flex items-center gap-2 flex-wrap" data-testid={`tpl-log-change-${it.id}-${i}`}>
                    <span className="text-slate-700 font-medium">{c.label}</span>
                    <span className="text-slate-400 line-through">{c.from}</span>
                    <span className="text-slate-400">→</span>
                    <span className="text-emerald-700 font-semibold">{c.to}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TemplateEditor({ tpl, setTpl, zh, onSave, busy, onCancel }) {
  const setTier = (ri, ti, v) => setTpl({ ...tpl, regions: tpl.regions.map((r, j) => j === ri ? { ...r, tiers: r.tiers.map((t, k) => k === ti ? { ...t, perSf: Number(v) } : t) } : r) });
  const setUp = (i, k, v) => setTpl({ ...tpl, upgrades: tpl.upgrades.map((u, j) => j === i ? { ...u, [k]: k === "price" ? Number(v) : v } : u) });
  const addUp = () => setTpl({ ...tpl, upgrades: [...tpl.upgrades, { key: `custom_${Date.now()}`, name: zh ? "新升级项" : "New upgrade", price: 0, unit: "flat", category: "other" }] });
  const delUp = (i) => setTpl({ ...tpl, upgrades: tpl.upgrades.filter((_, j) => j !== i) });
  const CAT_LABEL = zh
    ? { kitchen_bath: "厨卫", structure: "结构", electrical: "电气", exterior: "外观", other: "其他" }
    : { kitchen_bath: "Kitchen & Bath", structure: "Structure", electrical: "Electrical", exterior: "Exterior", other: "Other" };
  const CAT_ORDER = ["kitchen_bath", "structure", "electrical", "exterior", "other"];
  const roi = { grossYieldLow: 0.12, grossYieldHigh: 0.15, valueAddMultiple: 2, expenseRatio: 0.25, ...(tpl.roi || {}) };
  const setRoi = (k, v, pct) => setTpl({ ...tpl, roi: { ...roi, [k]: v === "" ? "" : (pct ? Number(v) / 100 : Number(v)) } });
  const pctVal = (v) => (v === "" || v == null ? "" : Math.round(Number(v) * 10000) / 100);
  return (
    <div className="flex-1 overflow-auto p-5 space-y-4" data-testid="estimate-template-editor">
      <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="tpl-roi-block">
        <p className="text-xs font-semibold text-violet-700">{zh ? "ADU 投资分析参数（影响报价面板 / 业主页 / PDF / AI 文案）" : "ROI parameters (estimate panel / owner page / PDF / AI text)"}</p>
        <p className="text-[10px] text-slate-500 mb-2">{zh ? "年租金 = 造价 × 毛收益率区间；回本 = 造价 ÷ 年租金（毛口径）；建成市值 = 造价 × 倍数；净增值 = 市值 − 造价；运营费比例仅用于参考字段「年净租金」" : "annual rent = cost × yield range; payback = cost ÷ annual rent; ADU value = cost × multiple; net gain = value − cost"}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[["grossYieldLow", zh ? "毛收益率下限 %" : "Yield low %", true, "tpl-roi-yield-low"], ["grossYieldHigh", zh ? "毛收益率上限 %" : "Yield high %", true, "tpl-roi-yield-high"],
            ["valueAddMultiple", zh ? "建成市值倍数 ×" : "Value multiple ×", false, "tpl-roi-multiple"], ["expenseRatio", zh ? "运营费比例 %（参考）" : "Expense ratio %", true, "tpl-roi-expense"]].map(([k, label, pct, tid]) => (
            <div key={k}>
              <label className="text-[10px] text-slate-400">{label}</label>
              <Input type="number" step={pct ? 0.5 : 0.1} min={0} value={pct ? pctVal(roi[k]) : (roi[k] ?? "")} onChange={(e) => setRoi(k, e.target.value, pct)} className="h-8" data-testid={tid} />
            </div>
          ))}
        </div>
        {(() => { const lo = Number(roi.grossYieldLow), hi = Number(roi.grossYieldHigh), m = Number(roi.valueAddMultiple); const bad = !(lo > 0 && lo <= hi && hi <= 1) || m < 1; return bad ? <p className="text-[11px] text-red-600 mt-1" data-testid="tpl-roi-invalid">{zh ? "需满足：0 < 下限 ≤ 上限 ≤ 100%，倍数 ≥ 1" : "Need 0 < low ≤ high ≤ 100%, multiple ≥ 1"}</p>
          : <p className="text-[11px] text-slate-500 mt-1" data-testid="tpl-roi-example">{zh ? `示例：造价 $300,000 → 月租金 $${Math.round(300000 * lo / 12).toLocaleString()}–$${Math.round(300000 * hi / 12).toLocaleString()} · 回本 ${(1 / hi).toFixed(1)}–${(1 / lo).toFixed(1)} 年 · 市值 $${Math.round(300000 * m).toLocaleString()} · 净增值 $${Math.round(300000 * m - 300000).toLocaleString()}` : `Example: $300,000 cost → rent $${Math.round(300000 * lo / 12).toLocaleString()}–$${Math.round(300000 * hi / 12).toLocaleString()}/mo · payback ${(1 / hi).toFixed(1)}–${(1 / lo).toFixed(1)} yr · value $${Math.round(300000 * m).toLocaleString()}`}</p>; })()}
      </div>
      <p className="text-xs font-semibold text-slate-500">{zh ? "分区档位单价 ($/sf)" : "Region tier price ($/sf)"}</p>
      {tpl.regions.map((r, ri) => (
        <div key={r.key} className="rounded-lg border border-slate-100 p-2">
          <p className="text-sm font-semibold text-slate-700 mb-1.5">{r.name}</p>
          <div className="grid grid-cols-3 gap-2">
            {r.tiers.map((t, ti) => (
              <div key={t.key}>
                <label className="text-[10px] text-slate-400">{t.name}</label>
                <Input type="number" value={t.perSf} onChange={(e) => setTier(ri, ti, e.target.value)} className="h-8" data-testid={`tpl-tier-${r.key}-${t.key}`} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between pt-2">
        <p className="text-xs font-semibold text-slate-500">{zh ? "升级项(按分类管理)" : "Upgrades (by category)"}</p>
        <button onClick={addUp} data-testid="tpl-add-upgrade" className="text-xs text-indigo-600 flex items-center gap-1"><Plus className="h-3.5 w-3.5" />{zh ? "添加" : "Add"}</button>
      </div>
      {CAT_ORDER.filter((c) => tpl.upgrades.some((u) => (u.category || "other") === c)).map((c) => (
        <div key={c} data-testid={`tpl-cat-${c}`}>
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{CAT_LABEL[c]}</p>
          {tpl.upgrades.map((u, i) => ((u.category || "other") === c ? (
            <div key={i} className="flex items-center gap-2 mb-1.5" data-testid={`tpl-upgrade-${u.key}`}>
              <Input value={u.name} onChange={(e) => setUp(i, "name", e.target.value)} className="h-8 flex-1" />
              <Input type="number" value={u.price} onChange={(e) => setUp(i, "price", e.target.value)} className="h-8 w-20" />
              <select value={u.unit} onChange={(e) => setUp(i, "unit", e.target.value)} className="h-8 rounded-md border border-slate-200 text-xs px-1">
                <option value="flat">{zh ? "固定" : "flat"}</option><option value="per_sf">/sf</option><option value="per_door">/门</option><option value="per_bath">/卫</option>
              </select>
              <select value={u.category || "other"} data-testid={`tpl-cat-select-${u.key}`} onChange={(e) => setUp(i, "category", e.target.value)} className="h-8 rounded-md border border-slate-200 text-xs px-1">
                {CAT_ORDER.map((cc) => <option key={cc} value={cc}>{CAT_LABEL[cc]}</option>)}
              </select>
              <button onClick={() => delUp(i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ) : null))}
        </div>
      ))}
      <TierSpecEditor tpl={tpl} setTpl={setTpl} zh={zh} />
      <div className="flex gap-2 pt-2">
        <Button onClick={onSave} disabled={busy} data-testid="tpl-save" className="bg-brand-500 hover:bg-brand-600 text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (zh ? "保存模板" : "Save template")}</Button>
        <Button variant="outline" onClick={onCancel}>{zh ? "取消" : "Cancel"}</Button>
      </div>
    </div>
  );
}
